import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';

import {
  READINESS_CHECK_DEEP_LINK,
  ReadinessCheckId,
  ReadinessCheckSeverity,
  ReadinessCheckStatus,
} from './readinessChecks.constants';

const { PASS, FAIL, MISSING_DATA } = ReadinessCheckStatus;
const { HARD, SOFT } = ReadinessCheckSeverity;

const SEVERITY_BY_ID = {
  [ReadinessCheckId.TRID_CLOCK]: SOFT,
  [ReadinessCheckId.E_CONSENT]: SOFT,
  [ReadinessCheckId.BORROWER_EMAILS]: HARD,
  [ReadinessCheckId.COMP_PLAN]: SOFT,
  [ReadinessCheckId.FEES_PRESENT]: HARD,
  [ReadinessCheckId.LOAN_FIELDS]: HARD,
};

const pluralizeBorrowers = count => (count === 1 ? 'borrower' : 'borrowers');

const buildTridClockCheck = ({ ecd, now }) => {
  const earliestClosingDate = ecd?.earliestClosingDate;

  if (!earliestClosingDate) {
    return {
      status: MISSING_DATA,
      title: 'Disclosure timing not yet computed',
      description: 'The earliest closing date will populate once loan terms are set.',
    };
  }

  const dueDate = dayjs(earliestClosingDate);
  const receivedDate = ecd?.applicationReceivedDate ? dayjs(ecd.applicationReceivedDate) : null;
  const chip =
    dueDate.isValid() && dueDate.startOf('day').diff(dayjs(now).startOf('day'), 'day') >= 0
      ? { label: `LE due by ${dueDate.format('MMM D')}`, variant: LabelVariant.BLUE }
      : null;

  return {
    status: PASS,
    title:
      receivedDate && receivedDate.isValid()
        ? `Application received ${receivedDate.format('MMM D')}`
        : 'TRID clock is running',
    description: 'TRID clock is running. Lender owns the 3-business-day issuance deadline.',
    chip,
  };
};

const buildEConsentCheck = ({ consentRecords }) => {
  const records = consentRecords ?? [];

  if (!records.length) {
    return {
      status: MISSING_DATA,
      title: 'E-consent status unavailable',
      description: "Borrower consent records haven't loaded yet.",
    };
  }

  const total = records.length;
  const consented = records.filter(({ eConsentDate }) => !!eConsentDate).length;

  if (consented === total) {
    return {
      status: PASS,
      title: `E-consent captured — ${total} of ${total} ${pluralizeBorrowers(total)}`,
      description: 'All signers can receive electronic delivery. E-sign is available.',
    };
  }

  const pending = total - consented;

  return {
    status: FAIL,
    title: `E-consent pending — ${consented} of ${total} ${pluralizeBorrowers(total)}`,
    description: `${pending} ${pluralizeBorrowers(
      pending,
    )} haven't consented to electronic delivery — mail delivery is still available.`,
  };
};

const buildBorrowerEmailsCheck = ({ borrowers }) => {
  const list = borrowers ?? [];

  if (!list.length) {
    return {
      status: MISSING_DATA,
      title: 'Borrower details unavailable',
      description: "Borrower records haven't loaded yet.",
    };
  }

  const emails = list.map(({ email }) => (email ?? '').trim()).filter(Boolean);

  if (emails.length < list.length) {
    return {
      status: FAIL,
      title: 'Borrower email missing',
      description: 'Every borrower needs a valid email address for electronic delivery.',
    };
  }

  const normalized = emails.map(email => email.toLowerCase());

  if (new Set(normalized).size < normalized.length) {
    return {
      status: FAIL,
      title: 'Duplicate borrower emails',
      description: 'Two borrowers share an email address — each signer needs a unique email.',
    };
  }

  return {
    status: PASS,
    title: 'Borrower emails verified',
    description: `${emails.join(' · ')} — no duplicates.`,
  };
};

const buildCompPlanCheck = ({ compensation }) => {
  if (compensation?.isResolved) {
    return {
      status: PASS,
      title: `Comp plan resolved${compensation.planLabel ? ` — ${compensation.planLabel}` : ''}`,
      description: 'Compensation locked. Broker cannot edit (Reg Z).',
    };
  }

  return {
    status: MISSING_DATA,
    title: 'Comp plan not resolved',
    description: "Broker compensation isn't finalized for this loan yet.",
  };
};

const buildFeesCheck = ({ fees }) => {
  if (!fees?.isLoaded) {
    return {
      status: MISSING_DATA,
      title: 'Fees not loaded yet',
      description: 'Fee data will populate once the fee worksheet loads.',
    };
  }

  if (fees.hasHomeownersInsurancePremium) {
    return {
      status: PASS,
      title: "Homeowner's insurance premium recorded (Prepaids)",
      description: 'Section F has an annual premium estimate.',
    };
  }

  return {
    status: FAIL,
    title: "Homeowner's insurance premium missing (Prepaids)",
    description: 'Section F requires an annual premium estimate before the LE can be generated.',
  };
};

const buildLoanFieldsCheck = ({ loanFields }) => {
  const hasAppraisedValue = loanFields?.hasAppraisedValue;

  if (hasAppraisedValue === undefined || hasAppraisedValue === null) {
    return {
      status: MISSING_DATA,
      title: 'Property value not loaded yet',
      description: 'Property details will populate once the loan loads.',
    };
  }

  if (hasAppraisedValue) {
    return {
      status: PASS,
      title: 'Property appraised value recorded',
      description: 'Used for the LTV and cash-to-close figures on the LE.',
    };
  }

  return {
    status: FAIL,
    title: 'Property appraised value missing',
    description: 'Needed for the LTV and cash-to-close figures shown on the LE.',
  };
};

const CHECK_BUILDERS = [
  { id: ReadinessCheckId.TRID_CLOCK, build: buildTridClockCheck },
  { id: ReadinessCheckId.E_CONSENT, build: buildEConsentCheck },
  { id: ReadinessCheckId.BORROWER_EMAILS, build: buildBorrowerEmailsCheck },
  { id: ReadinessCheckId.COMP_PLAN, build: buildCompPlanCheck },
  { id: ReadinessCheckId.FEES_PRESENT, build: buildFeesCheck },
  { id: ReadinessCheckId.LOAN_FIELDS, build: buildLoanFieldsCheck },
];

export const computeReadinessChecks = (input = {}) =>
  CHECK_BUILDERS.map(({ id, build }) => {
    const { chip = null, ...result } = build(input);

    return {
      id,
      severity: SEVERITY_BY_ID[id],
      chip,
      deepLink: READINESS_CHECK_DEEP_LINK[id] ?? null,
      ...result,
    };
  });

export const getBlockingCheckIds = (checks = []) =>
  checks.filter(({ severity, status }) => severity === HARD && status !== PASS).map(({ id }) => id);

export const summarizeReadiness = (checks = []) => {
  const passingCount = checks.filter(({ status }) => status === PASS).length;
  const blockingCount = checks.filter(
    ({ severity, status }) => severity === HARD && status !== PASS,
  ).length;

  return {
    total: checks.length,
    passingCount,
    blockingCount,
    isReadyToContinue: blockingCount === 0,
  };
};
